import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import xss from 'xss'
import { emoji } from 'Constants/emoji'
import useViewObserve from 'Components/hooks/use-view-observe'
import noopWrapper, { Wrapper } from 'lib/noop-wrapper'
import useExpandAutoScroll from 'Components/hooks/use-expand-auto-scroll'
import {
  preventDefault,
  stopPropagation,
} from 'Components/new/community-card/interact'
import { makeTopicUrl } from 'Components/new/links/topic-link'

export const QALabel: React.FC = ({ children }) => {
  return (
    <span className="tw-inline-flex tw-items-center tw-justify-center tw-align-text-bottom tw-px-4 tw-align-text-bottom tw-h-16 tw-mr-8 tw-bg-common-black tw-rounded-2 tw-text-white tw-text-12">
      {children}
    </span>
  )
}

export const HotLabel = () => {
  return (
    <React.Fragment>
      <span className="tw-inline-block tw-align-text-bottom tw-w-30 tw-h-16" />
      <style jsx>
        {`
          span {
            background: url(${require('static/hot-comment.svg')}) center/cover;
          }
        `}
      </style>
    </React.Fragment>
  )
}

// 精华标签
export const JHLabel = ({ type = 1 }) => {
  return (
    <React.Fragment>
      <span
        className={classNames(
          'tw-inline-block tw-align-text-bottom tw-h-16 tw-mr-4',
          {
            'tw-w-48': type === 1,
            'tw-w-68': type === 2,
          }
        )}
      />
      <style jsx>
        {`
          .tw-w-68 {
            background: url(${require('static/jinghua-s.png')}) center/cover;
          }
          .tw-w-48 {
            background: url(${require('static/jinghua.png')}) center/cover;
          }
        `}
      </style>
    </React.Fragment>
  )
}

export const TopicLabel: React.FC<{ id?: string }> = ({ id, children }) => {
  return (
    id ? (
      <a
        href={makeTopicUrl({ id })}
        className="tw-text-common-blue tw-mr-4"
        target={'_blank'}
      >
        {children}
      </a>
    ) : null
  ) as ReactElement
}

export const size20Class = 'tw-text-16 md:tw-text-18 xl:tw-text-20'
export const size18Class = 'tw-text-14 md:tw-text-16 xl:tw-text-18'
export const size16Class = 'tw-text-12 md:tw-text-14 xl:tw-text-16'
export const size14Class = 'tw-text-12 md:tw-text-14 xl:tw-text-14'

export interface IProps {
  title?: string
  content?: string
  prepend?: ReactNode
  append?: ReactNode
  linkWrapper?: Wrapper
  rows?: number
}

const Text: React.FC<IProps> = ({
  title,
  content,
  prepend,
  append,
  linkWrapper = noopWrapper,
  rows = 3,
}) => {
  const [hasBtn, setHasBtn] = useState(false)

  const { expanded, setExpanded, toggleExpand, rootRef } =
    useExpandAutoScroll<HTMLDivElement>()

  const handleExpand = stopPropagation(
    preventDefault(
      useCallback(() => {
        toggleExpand()
      }, [toggleExpand])
    )
  )

  useViewObserve(
    useCallback(() => {
      const el = rootRef.current
      if (el) {
        const curHasBtn = el.scrollHeight - 24 * rows > 3 // 部分浏览器有误差
        setHasBtn(curHasBtn)
        if (curHasBtn !== hasBtn) {
          setExpanded(false)
        }
      }
    }, [hasBtn, rows]),
    {
      getContainer: useCallback(() => rootRef.current, [content]),
    }
  )

  return (
    <React.Fragment>
      {title ? (
        <div className="tw-mt-12">
          {linkWrapper(
            <h3
              className={classNames(
                'tw-font-medium tw-leading-22 line-1',
                size18Class
              )}
            >
              {title}
            </h3>
          )}
        </div>
      ) : null}
      {content ? (
        <div
          className={classNames(
            'tw-flex tw-overflow-hidden',
            title ? 'tw-mt-6' : 'tw-mt-12'
          )}
          ref={rootRef}
        >
          <p
            className={classNames(
              'tw-relative tw-leading-22 tw-text-justify tw-whitespace-pre-wrap tw-break-words tw-w-full',
              size16Class,
              {
                expanded,
              }
            )}
          >
            {hasBtn ? (
              <button
                className="tw-inline-block tw-float-right tw-clear-both tw-cursor-pointer tw-ml-4"
                type={'button'}
                onClick={handleExpand}
              >
                <span className="btn tw-text-common-orange" />
                <i
                  className={classNames(
                    'DCD_Icon icon_triangle_down_12 tw-text-common-orange',
                    size16Class
                  )}
                />
                <i
                  className={classNames(
                    'DCD_Icon icon_triangle_up_12 tw-text-common-orange',
                    size16Class
                  )}
                />
              </button>
            ) : null}
            {prepend}
            {linkWrapper(
              <React.Fragment>
                <span
                  dangerouslySetInnerHTML={{
                    __html: emoji(xss.filterXSS(content || '')),
                  }}
                  className="tw-text-common-black"
                />
                {append}
              </React.Fragment>
            )}
          </p>
        </div>
      ) : null}
      <style jsx>
        {`
          p {
            max-height: ${rows * 1.5}em;
          }
        `}
      </style>
      <style jsx>
        {`
          p,
          h3 {
            line-height: 1.5;
          }
          p:before {
            content: '';
            float: right;
            width: 0;
            height: calc(100% - 1.5em);
          }
          p.expanded {
            max-height: none;
          }
          .icon_triangle_up_12 {
            display: none;
          }
          p.expanded:after {
            display: none;
          }
          p.expanded .icon_triangle_up_12 {
            display: inline;
          }
          p.expanded .icon_triangle_down_12 {
            display: none;
          }

          label:before {
            content: '';
            margin-right: 16px;
          }
          p.expanded label:before {
            content: '';
          }
          .btn:before {
            content: '展开';
          }
          p.expanded .btn:before {
            content: '收起';
          }
        `}
      </style>
    </React.Fragment>
  )
}

export default Text
